import React from 'react'
import { arrayOf, string } from 'prop-types'
import * as yup from 'yup'
import Form from 'react-formal'
import Moment from 'moment'
import momentLocalizer from 'react-widgets-moment'
import { Flex, Box, Heading, Text } from 'rebass'
import User from './components/IconUser'
import Paperclip from '../../vectors/IconPaperclip'
import FileFormInput from './components/FileFormInput'
import {
  Label,
  CenteredContentBox,
  Message,
  Input,
  Fieldset,
  Widget,
  UploadForm,
  UploadFormHeader,
  UploadFormHeading,
  UploadFormSubHeading,
  UploadFormHeaderIcon,
  ButtonLoading,
  ErrorText,
  StyledTick,
  SubmitLink
} from './components'
import { Spinner } from '../HeaderOmniSearch/components/Feedback'
import { createGlobalStyle } from 'styled-components'
import { GraphQLContext } from 'graphql-react'
import { trackEvent } from '../../../lib/tracker'

Moment.locale('en-gb')
momentLocalizer()

export const createModelSchema = (extraFields = {}) =>
  yup.object({
    email: yup
      .string()
      .default('')
      .required('Please enter an email address')
      .email('Please check your email address is correct')
      .max(255, 'Your email is over 255 characters'),
    firstName: yup
      .string()
      .default('')
      .required('Please enter your first name')
      .max(75, 'Your name is over 75 characters'),
    lastName: yup
      .string()
      .default('')
      .required('Please enter your last name')
      .max(75, 'Your last name is over 75 characters'),
    postcode: yup
      .string()
      .default('')
      .required('Please enter your postcode')
      .max(8, 'Your postcode is over 8 characters'),
    phone: yup
      .string()
      .required('Please enter a phone number')
      .min(11, 'Please check your number')
      .max(16, 'Your number is too long'),
    title: yup
      .string()
      .nullable()
      .default('')
      .required('Please choose a title'),
    ...extraFields
  })

Form.addInputTypes(require('react-formal-inputs'))

const GlobalStyles = createGlobalStyle`
  html {
    scroll-behavior: smooth;
  }
`

export default (query, schema, Fields) => {
  class ContentUploadForm extends React.Component {
    constructor(props) {
      super(props)
      this.state = {
        submitSuccess: false,
        submitError: false,
        data: {},
        titles: this.props.titles,
        errors: {},
        loading: false,
        formComplete: false,
        formValid: false,
        missingFields: [],
        errorMessage: ''
      }
      this.handleSubmit = this.handleSubmit.bind(this)
      this.handleChange = this.handleChange.bind(this)
      this.handleChangeEvent = this.handleChangeEvent.bind(this)
      this.validateFileInput = this.validateFileInput.bind(this)
    }

    submitForm(data, files) {
      return this.context.operate({
        fetchOptionsOverride(options) {
          options.url = `${process.env.GATSBY_API_URL}/graphql`
        },
        operation: { query, variables: { data, files } }
      })
    }

    validateFileInput(file) {
      if (!file || !file.length) {
        this.setState({
          errors: {
            files: 'Please select at least one file'
          }
        })
      } else {
        this.setState({
          errors: {
            files: null
          }
        })
      }
    }

    async handleSubmit(formData) {
      this.validateFileInput(this.state.data.files)
      const { files } = this.state.data
      if (!files || !files.length) return
      this.setState({ loading: true })

      let extra = {}

      if (this.props.applyExtra) {
        extra = this.props.applyExtra()
      }

      trackEvent(this.props.trackingTitle, 'Form', 'Submit', '', {})

      const { cacheKey } = this.submitForm(
        {
          ...formData,
          ...extra
        },
        files
      )

      const res = (result) => {
        if (result.cacheKey !== cacheKey) return

        if (result.cacheValue === undefined) return

        const { graphQLErrors } = result.cacheValue

        if (graphQLErrors && graphQLErrors.length) {
          let errorMessage = ''

          if (graphQLErrors[0].message.includes('is over 30MB')) {
            errorMessage = graphQLErrors[0].message
          }

          if (
            graphQLErrors[0].message.includes('Message length is more than')
          ) {
            errorMessage = 'Please make sure your files are less than 10MB'
          }

          this.setState({ submitError: true, loading: false, errorMessage })
        } else {
          this.setState({ submitSuccess: true, loading: false })
        }

        this.context.off('cache', res)
      }

      this.context.on('cache', res)
    }

    isFormComplete(data) {
      this.retrieveMissingFields(data)

      if (Object.keys(data).length < this.props.fieldCount) return false
      this.validateFileInput(data.files)

      return Object.values(data).every((value) => !!value)
    }

    handleChangeEvent(value, e) {
      const data = { ...this.state.data, [e.currentTarget.name]: value }
      const isFormComplete = this.isFormComplete(data)
      this.setState({
        submitError: false,
        submitSuccess: false,
        data,
        formComplete: isFormComplete
      })
    }

    handleChange(name, value) {
      const data = { ...this.state.data, [name]: value }
      const isFormComplete = this.isFormComplete(data)
      this.setState({
        submitError: false,
        submitSuccess: false,
        data,
        formComplete: isFormComplete
      })
    }

    formatFileName(filename) {
      const date = Moment().format('YYYYMMDD-HHmmss')
      const cleanFileName = filename.toLowerCase().replace(/[^a-z0-9]/g, '-')
      const newFileName = `${date}-${cleanFileName}`
      return newFileName.substring(0, 60)
    }

    async retrieveMissingFields(data) {
      let missingFields = []
      try {
        await schema.validate(data, { abortEarly: false })
      } catch (e) {
        missingFields = e.inner ? e.inner.map((inner) => inner.path) : []
      }

      this.setState({ missingFields })
    }

    render() {
      /* COPY_NEEDED:
      career application - thanks
      career application - error
      */
      if (this.state.submitSuccess) {
        return (
          <Box py={40} px={16} id="upload-success">
            <Heading as="h2" mb={2}>
              Thanks!
            </Heading>
            <Text>{this.props.submitSuccessText}</Text>
          </Box>
        )
      }

      return (
        <UploadForm
          className="f24form"
          schema={schema}
          onSubmit={this.handleSubmit}
          id="upload-form"
          data-f24name={this.props.f24name}
        >
          <GlobalStyles />
          <Fieldset>
            <Box px={16}>
              <UploadFormHeader>
                <UploadFormHeaderIcon muted={this.state.formComplete}>
                  {this.state.formComplete ? (
                    <StyledTick />
                  ) : (
                    <User alt="User" />
                  )}
                </UploadFormHeaderIcon>
                <Box>
                  <UploadFormSubHeading muted={this.state.formComplete}>
                    First Step
                  </UploadFormSubHeading>
                  <UploadFormHeading muted={this.state.formComplete}>
                    Contact details
                  </UploadFormHeading>
                </Box>
              </UploadFormHeader>
              <Flex flexWrap="wrap" width={'100%'} py={[10]}>
                <Box
                  width={[12 / 12, null, '28%']}
                  mr={[0, null, '8%']}
                  mb={20}
                >
                  <Label>Title</Label>
                  <Widget>
                    <Form.Field
                      placeholder="Please select"
                      name="title"
                      data={this.state.titles}
                      type="dropdownlist"
                      aria-label="You are required to select a title"
                      onChange={(value) => this.handleChange('title', value)}
                    />
                    <Message for="title" />
                  </Widget>
                </Box>
                <Box
                  width={[12 / 12, null, '28%']}
                  mr={[0, null, '8%']}
                  mb={20}
                >
                  <Label>First name</Label>
                  <Input
                    placeholder="Enter first name"
                    name="firstName"
                    aria-label="You are required to enter your first name"
                    onChange={this.handleChangeEvent}
                  />
                  <Message for="firstName" />
                </Box>
                <Box width={[12 / 12, null, '28%']} mb={20}>
                  <Label>Last name</Label>
                  <Input
                    placeholder="Enter last name"
                    name="lastName"
                    career
                    application
                    aria-label="You are required to enter your last name"
                    onChange={this.handleChangeEvent}
                  />
                  <Message for="lastName" />
                </Box>
                <Box
                  width={[12 / 12, null, '28%']}
                  mr={[0, null, '8%']}
                  mb={20}
                >
                  <Label>Postcode</Label>
                  <Input
                    placeholder="Enter postcode"
                    name="postcode"
                    aria-label="You are required to enter your postcode"
                    onChange={this.handleChangeEvent}
                  />
                  <Message for="postcode" />
                </Box>
                <Box
                  width={[12 / 12, null, '28%']}
                  mr={[0, null, '8%']}
                  mb={20}
                >
                  <Label>Phone number</Label>
                  <Input
                    placeholder="Enter phone number"
                    name="phone"
                    aria-label="You are required enter your phone number"
                    onChange={this.handleChangeEvent}
                  />
                  <Message for="phone" />
                </Box>
                <Box width={[12 / 12, null, '28%']} mb={20}>
                  <Label>Email address</Label>
                  <Input
                    placeholder="Enter email address"
                    name="email"
                    aria-label="Enter your email, this is optional"
                    onChange={this.handleChangeEvent}
                  />
                  <Message for="email" />
                </Box>
              </Flex>
              {Fields && (
                <Fields
                  handleChangeEvent={this.handleChangeEvent}
                  handleChange={(title, value) =>
                    this.handleChange(title, value)
                  }
                  formComplete={this.state.formComplete}
                />
              )}
              <UploadFormHeader>
                <UploadFormHeaderIcon muted={!!this.state.data.files}>
                  {this.state.data.files ? <StyledTick /> : <Paperclip />}
                </UploadFormHeaderIcon>
                <Box>
                  <UploadFormSubHeading muted={!!this.state.data.files}>
                    {Fields ? 'Final' : 'Second'} Step
                  </UploadFormSubHeading>
                  <UploadFormHeading muted={!!this.state.data.files}>
                    {this.props.fileUploadTitle}
                  </UploadFormHeading>
                </Box>
              </UploadFormHeader>
              <Flex justifyContent flexWrap="wrap" width={'100%'}>
                <Box width={[1, 3 / 4]} mb={20}>
                  <Text>{this.props.fileUploadSubtitle}</Text>
                </Box>
                <Box width={[1, 1 / 4]} mb={20} pt={10} alignSelf>
                  <FileFormInput name="files" onChange={this.handleChange} />
                  {this.state.errors.files && (
                    <span>{this.state.errors.files}</span>
                  )}
                </Box>
              </Flex>
              {this.state.submitError && (
                <CenteredContentBox py={20}>
                  <ErrorText>
                    {this.state.errorMessage || this.props.submitErrorText}
                  </ErrorText>
                </CenteredContentBox>
              )}
              <CenteredContentBox py={20}>
                <SubmitLink
                  thin
                  shadow
                  mt={10}
                  type="submit"
                  aria-label={this.props.submitButtonText}
                  disabled={
                    this.state.loading ||
                    !this.state.formComplete ||
                    this.state.errors.files
                  }
                >
                  {this.props.submitButtonText}
                  {this.state.missingFields.length > 0 && (
                    <span>
                      {`You're missing the following fields: ${this.state.missingFields.join(
                        ', '
                      )}`}
                    </span>
                  )}
                </SubmitLink>
                {this.state.loading && (
                  <ButtonLoading>
                    <Spinner alt={this.props.submitLoadingSpinner} />
                  </ButtonLoading>
                )}
              </CenteredContentBox>
            </Box>
          </Fieldset>
        </UploadForm>
      )
    }
  }

  ContentUploadForm.defaultProps = {
    f24name: 'upload-form'
  }
  ContentUploadForm.contextType = GraphQLContext

  ContentUploadForm.propTypes = {
    titles: arrayOf(string),
    fileUploadTitle: string,
    fileUploadSubtitle: string,
    submitButtonText: string,
    submitLoadingSpinner: string,
    submitErrorText: string,
    f24name: string
  }

  return ContentUploadForm
}
